import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  styled
} from '@mui/material';
import { getStatusRadixStyles } from '../utils/statusUtils';
import { statusLabel, subStatusLabel } from '../utils/webcheckinStatus';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
    cursor: 'pointer',
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

/**
 * External Partner Check-in Table component
 * @param {Object} props - Component props
 * @returns {JSX.Element} ExtPartnerCheckInTable component
 */
const ExtPartnerCheckInTable = ({
  checkIns = [],
  loading = false,
  error = null,
  pagination = {},
  onPageChange = () => {},
  onRowsPerPageChange = () => {}
}) => {
  const [sortBy, setSortBy] = React.useState('departureDate');
  const [sortDirection, setSortDirection] = React.useState('asc');

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

const handleCheckInClick = (checkIn) => {
  window.open(`/ext-check-in/${checkIn.subscriptionId}`, '_blank');
};

  const handleChangePage = (event, newPage) => {
    onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    onRowsPerPageChange(parseInt(event.target.value, 10));
  };

  // Format date and time
  const formatDateTime = (dateString) => {
    if (!dateString) return { date: '', time: '' };
    try {
      const date = new Date(dateString);
      return {
        date: date.toLocaleDateString(),
        time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
    } catch {
      return { date: dateString, time: '' };
    }
  };

  // Get status chip properties
  const getStatusChipProps = (status) => {
    const styles = getStatusRadixStyles(status);

    return {
      label: statusLabel(status),
      className: `${styles.bgColor} ${styles.textColor} border ${styles.borderColor}`
    };
  };

  return (
    <Paper sx={{ overflow: 'hidden', position: 'relative' }}>
      <TableContainer
        sx={{
          maxWidth: '100%',
          maxHeight: 'calc(100vh - 250px)',
          overflow: 'auto'
        }}
      >
        <Table
          size="small"
          stickyHeader
          aria-label="sticky table"
          sx={{
            tableLayout: 'fixed',
            '& .MuiTableCell-root': {
              padding: '6px 8px',
              fontSize: '0.75rem'
            }
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  backgroundColor: '#f9fafb',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  width: '80px'
                }}
              >
                <TableSortLabel
                  active={sortBy === 'subscriptionId'}
                  direction={sortBy === 'subscriptionId' ? sortDirection : 'asc'}
                  onClick={() => handleSort('subscriptionId')}
                >
                  Subscription ID
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#f9fafb',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  width: '100px'
                }}
              >
                Partner Name
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#f9fafb',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  width: '70px'
                }}
              >
                Airline
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#f9fafb',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  width: '80px'
                }}
              >
                Flight Number
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#f9fafb',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  width: '70px'
                }}
              >
                Sector
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#f9fafb',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  width: '84px'
                }}
              >
                <TableSortLabel
                  active={true}
                  direction="asc"
                  onClick={() => handleSort('departureDate')}
                  sx={{
                    '& .MuiTableSortLabel-icon': {
                      color: '#1976d2 !important',
                    }
                  }}
                >
                  Departure
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#f9fafb',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  width: '70px'
                }}
              >
                PNR
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#f9fafb',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  width: '100px'
                }}
              >
                Check-In Status
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: '#f9fafb',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  width: '140px'
                }}
              >
                Message
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', py: 1 }}>Loading...</Typography>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="error" sx={{ fontSize: '0.75rem', py: 1 }}>
                    {error}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : checkIns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', py: 1 }}>
                    No check-ins found matching your filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              checkIns.map((checkIn) => {
                const departureDateTime = formatDateTime(checkIn.departureDate);
                const statusProps = getStatusChipProps(checkIn.checkinStatus);

                return (
                  <StyledTableRow
                    key={checkIn.subscriptionId}
                    onClick={() => handleCheckInClick(checkIn)}
                  >
                    <TableCell sx={{ maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.7rem' }}>
                      {checkIn.subscriptionId}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.7rem' }}>
                      {checkIn.partnerName}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.7rem' }}>
                      {checkIn.airline}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.7rem' }}>
                      {checkIn.flightNumber}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.7rem' }}>
                      {checkIn.sector}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ overflow: 'hidden', maxWidth: '100%' }}>
                        <Typography variant="body2" noWrap sx={{ fontSize: '0.7rem' }}>
                          {departureDateTime.date}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.65rem' }}>
                          {departureDateTime.time}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 70, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.7rem' }}>
                      {checkIn.pnr}
                    </TableCell>
                    <TableCell>
                      <div className={`${statusProps.className} px-2 py-1 rounded text-xs font-medium border`}>
                        {statusProps.label}
                      </div>
                      {checkIn.checkinStatus?.toLowerCase() === 'completed' && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.25, fontSize: '0.65rem', color: 'primary.main', fontWeight: 500 }}>
                          {checkIn.boardingPassRatio}
                        </Typography>
                      )}
                      {checkIn.checkinSubStatus && checkIn.checkinSubStatus !== 'NONE' && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.25, fontSize: '0.65rem', color: 'primary.main', fontWeight: 500 }}>
                          {subStatusLabel(checkIn.checkinSubStatus)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 140 }}>
                      <Typography
                        variant="caption"
                        title={checkIn.statusMessage || ''}
                        sx={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.65rem',
                          color: checkIn.statusMessage ? 'text.secondary' : 'text.disabled'
                        }}
                      >
                        {checkIn.statusMessage || 'N/A'}
                      </Typography>
                    </TableCell>
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[50]}
        component="div"
        count={pagination.totalItems || 0}
        rowsPerPage={pagination.itemsPerPage || 50}
        page={pagination.pageNumber || 0}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default ExtPartnerCheckInTable;
